import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/auth-context'

interface ProtectedRouteProps {
  children: React.ReactNode
  permissions?: string[]
  roles?: string[]
  requireAll?: boolean
  redirectTo?: string
  fallback?: React.ReactNode
}

export function ProtectedRoute({
  children,
  permissions,
  roles,
  requireAll = false,
  redirectTo = '/unauthorized',
  fallback = <div className="flex h-screen items-center justify-center">Loading...</div>
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission, hasAnyRole, hasAllRoles } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true })
      return
    }

    if (!isLoading && isAuthenticated) {
      let hasAccess = true

      if (permissions) {
        hasAccess = requireAll
          ? permissions.every(p => hasPermission(p))
          : permissions.some(p => hasPermission(p))
      }

      if (hasAccess && roles) {
        hasAccess = requireAll
          ? hasAllRoles(roles)
          : hasAnyRole(roles)
      }

      if (!hasAccess) {
        navigate(redirectTo, { replace: true })
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    permissions,
    roles,
    requireAll,
    hasPermission,
    hasAnyRole,
    hasAllRoles,
    navigate,
    redirectTo
  ])

  if (isLoading) {
    return <>{fallback}</>
  }

  if (!isAuthenticated) {
    return null
  }

  if (permissions || roles) {
    let hasAccess = true

    if (permissions) {
      hasAccess = requireAll
        ? permissions.every(p => hasPermission(p))
        : permissions.some(p => hasPermission(p))
    }

    if (hasAccess && roles) {
      hasAccess = requireAll
        ? hasAllRoles(roles)
        : hasAnyRole(roles)
    }

    if (!hasAccess) {
      return null
    }
  }

  return <>{children}</>
}
